import React, { useEffect, useState, useMemo } from "react";
import {
  EditableInputComponent,
  EditableSelectComponent,
} from "components/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { FieldType } from "shared/types/field";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { Checkmark } from "assets/icons";
import { Input, Select, SelectItem, Button, Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from "@heroui/react";
import { CheckCircle, WarningCircle, PlusCircle, X, Recycle, FloppyDisk, TreeStructure, BracketsCurly, CurrencyEur, Brain, Package } from "@phosphor-icons/react";
import { DeleteLabelModal } from "./components/deleteLabelModal";
import { toast } from "react-toastify";
import { FinancialDocumentType } from "shared/types/renovation";
import { Modal, ModalContent, ModalHeader, ModalBody } from "@heroui/react";
import LabelAnalysisConfigPanel from "./components/LabelAnalysisConfigPanel";
import { LabelExtractionConfig } from "./components/LabelExtractionConfig";
import { PageInfoBanner } from "@/shared/components/walkthrough";


export enum DocumentRequirementType {
  REQUIRED = "vereist",
  OPTIONAL = "optioneel", 
  CONDITIONAL = "vereist als"
}

// Move the formatCondition function outside both components so it's available globally
const formatCondition = (condition: any) => {
  if (!condition || !condition.field || !condition.operator) return '';
  return `${condition.field} ${condition.operator} ${typeof condition.value === 'boolean' ? (condition.value ? 'Ja' : 'Nee') : condition.value}`;
};

/**
 * Cumulative document configuration - for labels where documents build on each other
 */
interface CumulativeConfig {
    approvalStatuses?: string[];    // e.g., ['verleend', 'goedgekeurd', 'vergund']
    enableTimeline?: boolean;       // Auto-enabled for cumulative
    enableSynthesis?: boolean;      // AI summary of document history
}

interface FileLabel {
    id: number;
    label: string;
    resourceType?: string;
    documentType?: string;
    isRenovationLabel: boolean;
    requiresFinancialDetails: boolean;
    financialDocumentType?: FinancialDocumentType;
    requirementType: DocumentRequirementType;
    conditionalRequirements?: any;
    folders?: any[];
    naturalLanguageAliases?: string[];
    // Document behavior - how multiple documents with this label behave
    documentBehavior?: 'replacing' | 'cumulative';
    cumulativeConfig?: CumulativeConfig | null;
    // AI Analysis fields
    aiAnalysisEnabled?: boolean;
    extractionPrompt?: string;
    extractionFields?: any[];
    validityRules?: any;
    conformityRules?: any;
    aiProvider?: string;
    aiModel?: string;
    // Asset detection
    isAssetSource?: boolean;
}

interface SimpleCondition {
  field: string;
  operator: string;
  value: any;
}

interface ConditionGroup {
  type: 'AND' | 'OR';
  conditions: (SimpleCondition | ConditionGroup)[];
}

const FileLabelManagementTab = () => {
  const [labels, setLabels] = useState<FileLabel[]>([]);
  // const [categories, setCategories] = useState([]);
  const { session } = useSession();
  const [editMode, setEditMode] = useState({});
  const [newLabel, setNewLabel] = useState("");
  const [tempLabelData, setTempLabelData] = useState<{ [key: string]: any }>({});
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState<any>(null); // Store the label to be deleted
  const [conditionalFieldModal, setConditionalFieldModal] = useState({
    isOpen: false,
    labelId: null as number | null,
    conditions: [] as any[],
    resourceType: 'building' as string
  });

  const [aiConfigModal, setAiConfigModal] = useState({
    isOpen: false,
    labelId: null as number | null,
    labelName: '' as string
  });

  // Track which label's extraction config is expanded (inline mode)
  const [expandedExtractionLabelId, setExpandedExtractionLabelId] = useState<number | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchLabelsAndCategories();
  }, []);

  const openDeleteModal = (label: any) => {
    setLabelToDelete(label); // Set the label to be deleted
    setDeleteModalOpen(true); // Open the modal
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false); // Close the modal
    setLabelToDelete(null); // Reset the selected label
  };


  const fetchLabelsAndCategories = async () => {
    try {
      const labelsRes = await fetchWithAuth(
        `${API_URL}/file/file-labels?includeRelations=true`,
        { method: "GET" },
        session,
      );
      // const categoriesRes = await fetchWithAuth(
      //   `${API_URL}/file/label-categories`,
      //   { method: "GET" },
      //   session,
      // );
      if (labelsRes.ok /* && categoriesRes.ok */) {
        const fetchedLabels = await labelsRes.json();
        const sortedLabels = fetchedLabels.sort((a: any, b: any) =>
          a.label.localeCompare(b.label),
        ); // Sort labels alphabetically
        setLabels(sortedLabels);
        console.log(sortedLabels);
        // setCategories(await categoriesRes.json());
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleLabelChange = async (id: any, newData: any) => {
    try {
      if (newData.requiresFinancialDetails && newData.financialDocumentType) {
        const existingLabel = labels.find(l => 
          l.id !== id && 
          l.isRenovationLabel && 
          l.requiresFinancialDetails && 
          l.financialDocumentType === newData.financialDocumentType
        );
        
        if (existingLabel) {
          toast.error(`Er bestaat al een renovatie label voor ${newData.financialDocumentType}`);
          return;
        }
      }

      const response = await fetchWithAuth(
        `${API_URL}/file/file-label/${id}`,
        {
          method: "PUT",
          body: JSON.stringify(newData),
          headers: { "Content-Type": "application/json" },
        },
        session,
      );
      if (response.ok) {
        fetchLabelsAndCategories(); // Refresh labels after update
      }
    } catch (error) {
      console.error("Error updating label:", error);
    }
  };

  const handleCreateLabel = async (newLabel: any) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/file-label`,
        {
          method: "POST",
          body: JSON.stringify(newLabel),
          headers: { "Content-Type": "application/json" },
        },
        session,
      );
      if (response.ok) {
        fetchLabelsAndCategories(); // Refresh labels after creation
      }
    } catch (error) {
      console.error("Error creating label:", error);
    }
  };

  // const handleCreateCategory = async (newCategory: any) => {
  //   try {
  //     const response = await fetchWithAuth(
  //       `${API_URL}/file/label-category`,
  //       {
  //         method: "POST",
  //         body: JSON.stringify(newCategory),
  //         headers: { "Content-Type": "application/json" },
  //       },
  //       session,
  //     );
  //     if (response.ok) {
  //       fetchLabelsAndCategories(); // Refresh categories after creation
  //     }
  //   } catch (error) {
  //     console.error("Error creating category:", error);
  //   }
  // };

  const toggleEditMode = (label: any) => {
    if (!(editMode as any)[label.id]) {
      // Entering edit mode
      setTempLabelData({
        ...tempLabelData,
        [label.id]: {
          label: label.label,
          resourceType: label.resourceType,
          documentType: label.documentType,
          isRenovationLabel: label.isRenovationLabel,
          requiresFinancialDetails: label.requiresFinancialDetails,
          requirementType: label.requirementType || DocumentRequirementType.OPTIONAL,
          conditionalRequirements: label.conditionalRequirements || [],
          naturalLanguageAliases: label.naturalLanguageAliases || [],
          documentBehavior: label.documentBehavior || 'replacing',
          cumulativeConfig: label.cumulativeConfig || null,
          isAssetSource: label.isAssetSource || false
        },
      });
    } else {
      // Exiting edit mode (without saving)
      const updatedTempData = { ...tempLabelData };
      delete updatedTempData[label.id]; // Remove the temporary data for this label
      setTempLabelData(updatedTempData);
    }
    setEditMode({ ...editMode, [label.id]: !(editMode as any)[label.id] });
  };

  const saveChanges = async (id: any) => {
    const updatedData = tempLabelData[id];
    if (updatedData) {
      await handleLabelChange(id, updatedData);
      toggleEditMode({ id }); // Exit edit mode
    }
  };

  const cancelEditMode = (id: any) => {
    setEditMode({ ...editMode, [id]: false });
    const updatedTempData = { ...tempLabelData };
    delete updatedTempData[id]; // Discard changes
    setTempLabelData(updatedTempData);
  };

  const handleDeleteLabel = async (id: any) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/file-label/${id}`,
        { method: "DELETE" },
        session
      );
      if (response.ok) {
        fetchLabelsAndCategories(); // Refresh the labels list after deletion
      }
    } catch (error) {
      console.error("Error deleting label:", error);
      throw error; // Let the calling function handle the error
    }
  };





  const handleNewLabelSubmit = async () => {
    if (newLabel) {
      await handleCreateLabel({ label: newLabel, category: null });
      setNewLabel("");
    }
  };


  const handleRequirementTypeChange = (labelId: number, requirementType: DocumentRequirementType) => {
    // First, check if we're in edit mode - if not, enter edit mode
    if (!(editMode as any)[labelId]) {
      // Get the current label
      const label = labels.find(l => l.id === labelId);
      if (label) {
        // Enter edit mode and initialize temp data
        setTempLabelData({
          ...tempLabelData,
          [labelId]: {
            label: label.label,
            resourceType: label.resourceType,
            documentType: label.documentType,
            isRenovationLabel: label.isRenovationLabel,
            requiresFinancialDetails: label.requiresFinancialDetails,
            financialDocumentType: label.financialDocumentType,
            requirementType: requirementType, // Set to the new value
            conditionalRequirements: label.conditionalRequirements || [],
            naturalLanguageAliases: label.naturalLanguageAliases || [],
            documentBehavior: label.documentBehavior || 'replacing',
            cumulativeConfig: label.cumulativeConfig || null
          }
        });
        setEditMode({ ...editMode, [labelId]: true });
      }
    } else {
      // Already in edit mode, just update the requirementType
      setTempLabelData({
        ...tempLabelData,
        [labelId]: {
          ...tempLabelData[labelId],
          requirementType
        }
      });
    }
    
    // If switching to conditional, immediately open the modal to set conditions
    if (requirementType === DocumentRequirementType.CONDITIONAL) {
      const label = labels.find(l => l.id === labelId);
      if (label) {
        openConditionalFieldsModal(
          labelId,
          label.conditionalRequirements || [],
          label.resourceType || 'building'
        );
      }
    }
  };

  // Define the openConditionalFieldsModal function
  const openConditionalFieldsModal = (labelId: number, conditions: any[] = [], resourceType: string = 'building') => {
    setConditionalFieldModal({
      isOpen: true,
      labelId,
      conditions: conditions || [],
      resourceType: resourceType || 'building'
    });
  };

  // Fix the save conditional requirements function to properly update the state
  const handleSaveConditionalRequirements = async (labelId: number | null, conditions: any[]) => {
    if (labelId === null) {
      toast.error("Geen label ID gevonden");
      return;
    }
    
    try {
      setIsLoading(true);
      console.log("Saving conditions:", conditions);
      
      // Make the PATCH request to update the conditionalRequirements
      const response = await fetchWithAuth(
        `${API_URL}/file/file-label/${labelId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            conditionalRequirements: conditions,
            requirementType: DocumentRequirementType.CONDITIONAL 
          })
        },
        session
      );
      
      if (response.ok) {
        // Update the local state
        const updatedLabels = labels.map(label => {
          if (label.id === labelId) {
            return {
              ...label,
              conditionalRequirements: conditions,
              requirementType: DocumentRequirementType.CONDITIONAL
            };
          }
          return label;
        });
        
        setLabels(updatedLabels);
        toast.success("Voorwaarden opgeslagen");
        
        // Clear any temporary data that might cause overwriting
        const newTempData = { ...tempLabelData };
        delete newTempData[labelId];
        setTempLabelData(newTempData);
        
        // Fetch updated labels, but don't depend on this for UI update
        fetchLabelsAndCategories();
      } else {
        toast.error("Fout bij opslaan van voorwaarden");
      }
    } catch (error) {
      console.error("Error saving conditional requirements:", error);
      toast.error("Fout bij opslaan van voorwaarden");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshLabels = async () => {
    setIsLoading(true);
    try {
        // Call your backend endpoint to clear the cache
        await fetch(`${API_URL}/file/force-refresh-missing-documents`, { 
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
            }
        });
        
        // Refetch labels
        await fetchLabels();
        toast.success('Labels refreshed successfully');
    } catch (error) {
        console.error('Error refreshing labels:', error);
        toast.error('Failed to refresh labels');
    } finally {
        setIsLoading(false);
    }
  };

  // Add function to get auth token
  const getToken = () => {
    return session?.getToken() || '';
  };
  
  // Add function to fetch labels
  const fetchLabels = async () => {
    await fetchLabelsAndCategories();
  };

  return (
    <div className="flex flex-col gap-8">
      <PageInfoBanner pageId="file-labels" />

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="flex flex-1 items-center gap-2">
          {/* <input
            className="w-full rounded-sm border p-2"
            type="text"
            placeholder="Nieuw bestandstype"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          /> */}
          <Input
            placeholder="Vul in..."
            label="Label toevoegen"
            labelPlacement="inside"
            onChange={(e) => setNewLabel(e.target.value)}
            value={newLabel}
            endContent={
              <button
                className={`rounded-full p-1 text-white ${newLabel
                  ? "bg-moss-primary"
                  : "pointer-events-none bg-moss-primary/50"
                  }`}
                onClick={handleNewLabelSubmit}
                disabled={!newLabel}
              >
                <Checkmark className="h-4 w-4" />
              </button>
            }
            classNames={{
              input:
                "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
              inputWrapper:
                "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element! data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv! group-data-[focus=true]:shadow-form-element-fc!",
            }}
          />
        </div>
        <div className="flex flex-1 items-center gap-4">
          {/* <input
            className="w-full rounded-sm border p-2"
            type="text"
            placeholder="Nieuwe categorie voor bestanden"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
          />
          <button
            className="mt-2 rounded-sm bg-green-500 px-4 py-2 text-white hover:bg-green-600"
            onClick={handleNewCategorySubmit}
          >
            Voeg categorie toe
          </button> */}
          {/* <Input
            placeholder="Vul in..."
            label="Bestandscategorie toevoegen"
            labelPlacement="inside"
            onChange={(e) => setNewCategory(e.target.value)}
            value={newCategory}
            endContent={
              <button
                className={`rounded-full p-1 text-white ${newCategory
                  ? "bg-moss-primary"
                  : "pointer-events-none bg-moss-primary/50"
                  }`}
                onClick={handleNewCategorySubmit}
                disabled={!newCategory}
              >
                <Checkmark className="h-4 w-4" />
              </button>
            }
            classNames={{
              input:
                "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
              inputWrapper:
                "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element! data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv! group-data-[focus=true]:shadow-form-element-fc!",
            }}
          /> */}
        </div>
      </div>
      <table data-tour="file-label-table" className="w-full table-auto border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="border p-2">Bestandstype</th>
            <th className="border p-2">Gebouw of unitlabel?</th>
            <th className="border p-2">Document Type</th>
            <th className="border p-2">Financieel renovatiedocument</th>
            <th className="border p-2">Document vereist in Due Diligence</th>
            <th data-tour="file-label-ai-toggle" className="border p-2">AI Analyse</th>
            <th className="border p-2">Voorziening bron</th>
            <th className="border p-2">Acties</th>
            <th className="border p-2">Gelinkt aan folder</th>
          </tr>
        </thead>
        <tbody>
          {labels.map((label: any) => (
            <React.Fragment key={label.id}>
            <tr>
              <td className="border p-2">
                <EditableInputComponent
                  type={FieldType.DEFAULT}
                  editMode={(editMode as any)[label.id]}
                  value={(editMode as any)[label.id] ? tempLabelData[label.id]?.label : label.label}
                  onChange={(newValue) =>
                    setTempLabelData({
                      ...tempLabelData,
                      [label.id]: { ...tempLabelData[label.id], label: newValue },
                    })
                  }
                  isLoading={isLoading}
                />
              </td>

              {/* <td className="border p-2">
                <EditableSelectComponent
                  editMode={(editMode as any)[label.id]}
                  value={label.category?.id || ""}
                  options={categories.map((c: any) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  onChange={(newCategoryId) =>
                    handleLabelChange(label.id, { category: newCategoryId })
                  }
                  isLoading={isLoading}
                />
              </td> */}
              <td className="border p-2">
                <EditableSelectComponent
                  editMode={(editMode as any)[label.id]}
                  value={(editMode as any)[label.id] ? tempLabelData[label.id]?.resourceType || "" : label.resourceType || ""}
                  options={[
                    { value: "building", label: "Gebouw" },
                    { value: "unit", label: "Unit" },
                    { value: "attribute", label: "Attribuut" },
                  ]}
                  onChange={(newResourceType) =>
                    setTempLabelData({
                      ...tempLabelData,
                      [label.id]: { ...tempLabelData[label.id], resourceType: newResourceType },
                    })
                  }
                  isLoading={isLoading}
                />
              </td>
              <td className="border p-2">
                <EditableSelectComponent
                  editMode={(editMode as any)[label.id]}
                  value={(editMode as any)[label.id] ? tempLabelData[label.id]?.documentType || "" : label.documentType || ""}
                  options={[
                    { value: "document", label: "Document" },
                    { value: "picture", label: "Foto" },
                  ]}
                  onChange={(newDocumentType) =>
                    setTempLabelData({
                      ...tempLabelData,
                      [label.id]: { ...tempLabelData[label.id], documentType: newDocumentType },
                    })
                  }
                  isLoading={isLoading}
                />
              </td>
              {/* Financieel renovatiedocument column */}
              <td className="border p-2">
                <div className="flex items-center justify-center">
                  {(editMode as any)[label.id] ? (
                    <button
                      onClick={() => {
                        setTempLabelData({
                          ...tempLabelData,
                          [label.id]: {
                            ...tempLabelData[label.id],
                            requiresFinancialDetails: !tempLabelData[label.id]?.requiresFinancialDetails
                          }
                        });
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                        tempLabelData[label.id]?.requiresFinancialDetails
                          ? 'bg-moss-primary text-white'
                          : 'bg-neutral-100 text-neutral-secondary-mid hover:bg-neutral-200'
                      }`}
                    >
                      <CurrencyEur size={16} />
                      <span className="text-sm">
                        {tempLabelData[label.id]?.requiresFinancialDetails ? 'Ja' : 'Nee'}
                      </span>
                    </button>
                  ) : (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                      label.requiresFinancialDetails
                        ? 'bg-moss-primary/10 text-moss-primary'
                        : 'bg-neutral-50 text-neutral-secondary-mid'
                    }`}>
                      <CurrencyEur size={16} />
                      <span className="text-sm">
                        {label.requiresFinancialDetails ? 'Ja' : 'Nee'}
                      </span>
                    </div>
                  )}
                </div>
              </td>
              <td className="border p-2">
                <div className="flex flex-col">
                  <div className="flex items-center mb-2">
                    <span className="text-sm mr-2">Vereist:</span>
                    <div className="flex space-x-2">
                      <button
                        className={`px-2 py-1 text-xs rounded-md ${
                          (tempLabelData[label.id]?.requirementType || label.requirementType) === DocumentRequirementType.REQUIRED
                            ? 'bg-moss-primary text-white'
                            : 'bg-neutral-100 text-neutral-primary'
                        }`}
                        onClick={() => handleRequirementTypeChange(label.id, DocumentRequirementType.REQUIRED)}
                      >
                        Altijd
                      </button>
                      <button
                        className={`px-2 py-1 text-xs rounded-md ${
                          (tempLabelData[label.id]?.requirementType || label.requirementType) === DocumentRequirementType.OPTIONAL
                            ? 'bg-neutral-secondary-mid text-white'
                            : 'bg-neutral-100 text-neutral-primary'
                        }`}
                        onClick={() => handleRequirementTypeChange(label.id, DocumentRequirementType.OPTIONAL)}
                      >
                        Optioneel
                      </button>
                      <button
                        className={`px-2 py-1 text-xs rounded-md flex items-center ${
                          (tempLabelData[label.id]?.requirementType || label.requirementType) === DocumentRequirementType.CONDITIONAL
                            ? 'bg-brick-primary text-white'
                            : 'bg-neutral-100 text-neutral-primary'
                        }`}
                        onClick={() => handleRequirementTypeChange(label.id, DocumentRequirementType.CONDITIONAL)}
                      >
                        Voorwaardelijk
                        {(tempLabelData[label.id]?.requirementType || label.requirementType) === DocumentRequirementType.CONDITIONAL && (
                          <button
                            className="ml-1 bg-white bg-opacity-20 rounded-sm p-0.5"
                            onClick={(e) => {
                              e.stopPropagation();
                              openConditionalFieldsModal(
                                label.id,
                                label.conditionalRequirements || [],
                                label.resourceType || 'building'
                              );
                            }}
                          >
                            <PlusCircle size={12} />
                          </button>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Show hint about saving if in edit mode */}
                  {(editMode as any)[label.id] && (
                    <div className="text-xs text-brick-primary flex items-center mt-1 mb-2">
                      <FloppyDisk size={12} className="mr-1" />
                      Klik op "Opslaan" om wijzigingen te bewaren
                    </div>
                  )}
                
                  {/* Always show conditions if the requirement type is conditional */}
                  {label.requirementType === DocumentRequirementType.CONDITIONAL && label.conditionalRequirements?.length > 0 && (
                    <div className="mt-2 text-xs bg-neutral-50 p-2 rounded-sm border border-neutral-100">
                      {label.conditionalRequirements.map((condition: any, idx: number) => (
                        <div key={idx} className="mb-1 last:mb-0 flex items-start">
                          <div className="h-2 w-2 mt-1 mr-1 rounded-full bg-brick-primary shrink-0"></div>
                          <span className="text-neutral-primary break-all">
                            {formatCondition(condition)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
              {/* AI Analyse column */}
              <td className="border p-2">
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => setExpandedExtractionLabelId(
                      expandedExtractionLabelId === label.id ? null : label.id
                    )}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                      expandedExtractionLabelId === label.id
                        ? 'bg-violet-600 text-white'
                        : label.aiAnalysisEnabled
                          ? 'bg-moss-primary text-white'
                          : 'bg-neutral-100 text-neutral-secondary-mid hover:bg-neutral-200'
                    }`}
                    title="AI Extractie configureren"
                  >
                    <Brain size={16} />
                    <span className="text-sm">
                      {expandedExtractionLabelId === label.id
                        ? 'Sluiten'
                        : label.aiAnalysisEnabled
                          ? 'Actief'
                          : 'Inactief'}
                    </span>
                  </button>
                </div>
              </td>
              {/* Voorziening bron column */}
              <td className="border p-2">
                <div className="flex items-center justify-center" title="Documenten met dit label worden gescand op voorzieningen (merk, model, serienummer)">
                  {(editMode as any)[label.id] ? (
                    <button
                      onClick={() => {
                        setTempLabelData({
                          ...tempLabelData,
                          [label.id]: {
                            ...tempLabelData[label.id],
                            isAssetSource: !tempLabelData[label.id]?.isAssetSource
                          }
                        });
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                        tempLabelData[label.id]?.isAssetSource
                          ? 'bg-moss-primary text-white'
                          : 'bg-neutral-100 text-neutral-secondary-mid hover:bg-neutral-200'
                      }`}
                    >
                      <Package size={16} />
                      <span className="text-sm">
                        {tempLabelData[label.id]?.isAssetSource ? 'Ja' : 'Nee'}
                      </span>
                    </button>
                  ) : (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                      label.isAssetSource
                        ? 'bg-moss-primary/10 text-moss-primary'
                        : 'bg-neutral-50 text-neutral-secondary-mid'
                    }`}>
                      <Package size={16} />
                      <span className="text-sm">
                        {label.isAssetSource ? 'Ja' : 'Nee'}
                      </span>
                    </div>
                  )}
                </div>
              </td>
              <td className="flex space-x-2 border p-2">
                <button
                  className="rounded-sm bg-yellow-500 px-4 py-1 text-white hover:bg-yellow-600"
                  onClick={() =>
                    (editMode as any)[label.id] ? saveChanges(label.id) : toggleEditMode(label)
                  }
                >
                  {(editMode as any)[label.id] ? "Opslaan" : "Bewerk"}
                </button>
                {(editMode as any)[label.id] && (
                  <button
                    className="rounded-sm bg-red-500 px-4 py-1 text-white hover:bg-red-600"
                    onClick={() => cancelEditMode(label.id)}
                  >
                    Annuleren
                  </button>
                )}
                <button
                  className="rounded-sm bg-red-600 px-4 py-1 text-white hover:bg-red-700"
                  onClick={() => openDeleteModal(label)} // Open the modal with the selected label
                >
                  Verwijderen
                </button>
              </td>
              <td className="border p-2">
                {label.folders && label.folders.length != 0 ? <CheckCircle className="h-6 w-6 text-moss-primary" /> : <WarningCircle className="h-6 w-6 text-brick-primary" />}
              </td>
            </tr>
            {/* Expandable extraction config row */}
            {expandedExtractionLabelId === label.id && (
              <tr key={`${label.id}-extraction`}>
                <td colSpan={10} className="border p-0 bg-neutral-50">
                  <div className="p-4">
                    <LabelExtractionConfig
                      labelId={label.id}
                      labelName={label.label}
                      autoExpand={true}
                    />
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
          ))}
        </tbody>
      </table>
      
      {/* Conditional Fields Modal */}
      <Modal 
        isOpen={conditionalFieldModal.isOpen} 
        onClose={() => setConditionalFieldModal(prev => ({ ...prev, isOpen: false }))}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader className="border-b pb-3">Configureer voorwaarden</ModalHeader>
          <ModalBody className="max-h-[70vh] overflow-y-auto">
            {conditionalFieldModal.labelId && (
              <ConditionalRequirementsEditor 
                conditions={conditionalFieldModal.conditions} 
                resourceType={conditionalFieldModal.resourceType}
                onSave={(updatedConditions) => {
                  handleSaveConditionalRequirements(conditionalFieldModal.labelId, updatedConditions);
                  setConditionalFieldModal(prev => ({ ...prev, isOpen: false }));
                }} 
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      
      {/* Existing modals */}
      {labelToDelete && (
        <DeleteLabelModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          labelName={labelToDelete.label}
          labelId={labelToDelete.id}
          handleDelete={handleDeleteLabel}
        />
      )}

      {/* AI Analysis Config Modal */}
      {aiConfigModal.labelId && (
        <LabelAnalysisConfigPanel
          labelId={aiConfigModal.labelId}
          labelName={aiConfigModal.labelName}
          isOpen={aiConfigModal.isOpen}
          onClose={() => {
            setAiConfigModal({ isOpen: false, labelId: null, labelName: '' });
            fetchLabelsAndCategories(); // Refresh labels after config change
          }}
        />
      )}

      <Button 
        onClick={refreshLabels}
        isLoading={isLoading}
      >
        <Recycle className="w-4 h-4 mr-2" />
        Refresh Labels
      </Button>
    </div>
  );
};

// Update the ConditionalRequirementsEditor component
const ConditionalRequirementsEditor = ({ 
  conditions = [], 
  resourceType = 'building', 
  onSave 
}: { 
  conditions: any; 
  resourceType: string; 
  onSave: (conditions: any) => void; 
}) => {
  // Initialize with the provided conditions or a default OR group with one empty condition
  const [rootConditionGroup, setRootConditionGroup] = useState<ConditionGroup>(() => {
    // Check if conditions is already in the new format
    if (conditions && typeof conditions === 'object' && 'type' in conditions) {
      return conditions as ConditionGroup;
    } 
    // Check if it's the old array format
    else if (Array.isArray(conditions) && conditions.length > 0) {
      return {
        type: 'OR',
        conditions: conditions as SimpleCondition[]
      };
    } 
    // Default structure
    else {
      return {
        type: 'OR',
        conditions: [{ field: '', operator: '==', value: '' }]
      };
    }
  });
  
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Available field options for building/unit
  const availableFields = useMemo(() => {
    // Define your fields based on the building and unit entities...
    if (resourceType === 'building') {
      return [
        { id: 'commonInstallationsInfo.elevatorPresentStatus', name: 'Lift Status', type: 'enum', values: [
          'Lift aanwezig, in dienst', 
          'Lift aanwezig, uit dienst', 
          'Geen lift'
        ]},
        { id: 'waterBuildingInfo.rainwaterTankPresent', name: 'Regenwaterput aanwezig', type: 'boolean' },
        { id: 'buildingOwnershipInfo.isMonument', name: 'Is monument', type: 'boolean' },
        // Add more building fields
      ];
    } else {
      return [
        { id: 'unitType', name: 'Unit Type', type: 'enum', values: ['Residentieel', 'Commercieel', 'Overig'] },
        { id: 'unitSubtype', name: 'Unit Subtype', type: 'enum', values: [
          'Winkel', 'Appartement', 'Studentenkamer', 'Studio', 'Kantoor', 'Duplex', 
          'Horeca', 'Woning', 'Garage', 'Fietsenberging', 'Berging', 'Buitenstaanplaats'
        ]},
        // Add more unit fields
      ];
    }
  }, [resourceType]);

  // Validate the condition group recursively
  const validateConditionGroup = (group: ConditionGroup, path = ''): string[] => {
    const errors: string[] = [];
    
    group.conditions.forEach((condition, index) => {
      const currentPath = path ? `${path}.${index}` : `${index}`;
      
      if ('type' in condition) {
        // It's a nested group - validate recursively
        errors.push(...validateConditionGroup(condition as ConditionGroup, currentPath));
      } else {
        // It's a simple condition
        const simpleCondition = condition as SimpleCondition;
        
        if (!simpleCondition.field) {
          errors.push(`Voorwaarde ${currentPath}: Veld is vereist`);
        }
        if (!simpleCondition.operator) {
          errors.push(`Voorwaarde ${currentPath}: Operator is vereist`);
        }
        if (simpleCondition.value === undefined || simpleCondition.value === null || simpleCondition.value === '') {
          errors.push(`Voorwaarde ${currentPath}: Waarde is vereist`);
        }
      }
    });
    
    return errors;
  };

  // Validate all conditions
  const validateConditions = (): boolean => {
    const errors = validateConditionGroup(rootConditionGroup);
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Save the conditions if validation passes
  const handleSave = () => {
    if (validateConditions()) {
      onSave(rootConditionGroup);
    }
  };

  // Render a condition group with its nested conditions
  const renderConditionGroup = (
    group: ConditionGroup, 
    path: number[] = [],
    canDelete: boolean = false
  ) => {
    return (
      <div className="border border-dashed border-neutral-300 p-3 rounded-lg mb-2">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center">
            <Chip 
              className={group.type === 'AND' ? 'bg-moss-primary text-white' : 'bg-brick-primary text-white'}
              size="sm"
            >
              {group.type}
            </Chip>
            <span className="text-xs ml-2 text-neutral-secondary-mid">
              {path.length > 0 ? `Groep ${path.join('.')}` : 'Hoofdgroep'}
            </span>
          </div>
          
          <div className="flex gap-2">
            <Dropdown>
              <DropdownTrigger>
                <Button 
                  size="sm"
                  variant="flat" 
                  className="bg-neutral-100"
                  isIconOnly
                >
                  <BracketsCurly size={14} />
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Group Actions">
                <DropdownItem
                  key="toggle-type"
                  onClick={() => toggleGroupType(path)}
                >
                  Wissel naar {group.type === 'AND' ? 'OR' : 'AND'}
                </DropdownItem>
                <DropdownItem
                  key="add-condition"
                  onClick={() => addCondition(path)}
                >
                  Voeg voorwaarde toe
                </DropdownItem>
                <DropdownItem
                  key="add-group"
                  onClick={() => addNestedGroup(path)}
                >
                  Voeg geneste groep toe
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            
            {canDelete && (
              <Button
                size="sm"
                color="danger"
                isIconOnly
                variant="flat"
                onClick={() => removeGroup(path)}
              >
                <X size={14} />
              </Button>
            )}
          </div>
        </div>
        
        <div className="pl-2 border-l-2 border-l-neutral-200">
          {group.conditions.map((condition, index) => {
            const currentPath = [...path, index];
            
            if ('type' in condition) {
              // It's a nested group
              return renderConditionGroup(
                condition as ConditionGroup, 
                currentPath,
                true
              );
            } else {
              // It's a simple condition
              return renderSimpleCondition(
                condition as SimpleCondition, 
                currentPath,
                group.conditions.length > 1
              );
            }
          })}
        </div>
      </div>
    );
  };

  // Render a simple condition with field, operator, value inputs
  const renderSimpleCondition = (
    condition: SimpleCondition, 
    path: number[],
    canDelete: boolean = false
  ) => {
    const selectedField = availableFields.find(f => f.id === condition.field);
    
    return (
      <div key={path.join('.')} className="flex items-center gap-2 w-full mb-2">
        <Select
          label="Veld"
          placeholder="Selecteer veld"
          selectedKeys={condition.field ? [condition.field] : []}
          onChange={(e) => updateCondition(path, 'field', e.target.value)}
          className="min-w-[200px] grow"
          classNames={{
            trigger: "min-w-[200px] w-full",
            value: "whitespace-normal break-words"
          }}
        >
          {availableFields.map(field => (
            <SelectItem key={field.id} textValue={field.name}>
              <div className="flex flex-col">
                <span>{field.name}</span>
                <span className="text-xs text-neutral-secondary-mid">({field.type})</span>
              </div>
            </SelectItem>
          ))}
        </Select>
        
        <Select
          label="Operator"
          selectedKeys={[condition.operator || '==']}
          onChange={(e) => updateCondition(path, 'operator', e.target.value)}
          className="min-w-[120px] w-auto"
          classNames={{
            trigger: "min-w-[120px] whitespace-normal",
            value: "whitespace-normal break-words",
            popoverContent: "min-w-[120px]"
          }}
        >
          <SelectItem key="==">==</SelectItem>
          <SelectItem key="!=">!=</SelectItem>
          <SelectItem key=">">{'>'}</SelectItem>
          <SelectItem key=">=">{'≥'}</SelectItem>
          <SelectItem key="<">{'<'}</SelectItem>
          <SelectItem key="<=">{'≤'}</SelectItem>
        </Select>
        
        {condition.field && selectedField?.type === 'enum' ? (
          <Select
            label="Waarde"
            placeholder="Selecteer waarde"
            selectedKeys={condition.value ? [condition.value] : []}
            onChange={(e) => updateCondition(path, 'value', e.target.value)}
            className="min-w-[200px] grow"
            classNames={{
              trigger: "min-w-[200px] w-full",
              value: "whitespace-normal break-words"
            }}
          >
            {selectedField.values && selectedField.values.map((val: any) => (
              <SelectItem key={val} textValue={val}>
                {val}
              </SelectItem>
            )) as any}
          </Select>
        ) : condition.field && selectedField?.type === 'boolean' ? (
          <Select
            label="Waarde"
            placeholder="Selecteer waarde"
            selectedKeys={condition.value !== undefined ? [String(condition.value)] : []}
            onChange={(e) => updateCondition(path, 'value', e.target.value === 'true')}
            className="min-w-[120px] grow"
          >
            <SelectItem key="true">Ja</SelectItem>
            <SelectItem key="false">Nee</SelectItem>
          </Select>
        ) : (
          <Input
            label="Waarde"
            placeholder="Voer waarde in"
            value={condition.value || ''}
            onChange={(e) => updateCondition(path, 'value', e.target.value)}
            className="min-w-[150px] grow"
            type={selectedField?.type === 'number' ? 'number' : 'text'}
          />
        )}
        
        {canDelete && (
          <Button 
            size="sm"
            isIconOnly
            color="danger"
            variant="flat"
            className="mt-6"
            onClick={() => removeCondition(path)}
          >
            <X size={14} />
          </Button>
        )}
      </div>
    );
  };

  // Update a specific condition by path
  const updateCondition = (path: number[], key: string, value: any) => {
    setRootConditionGroup(prevGroup => {
      const newGroup = structuredClone(prevGroup);
      let current: any = newGroup;
      
      // Navigate to the correct nested position
      for (let i = 0; i < path.length - 1; i++) {
        const idx = path[i];
        parent = current;
        current = current.conditions[idx];
      }
      
      // Update the condition
      if (path.length === 0) {
        // Special case: updating the root group
        (newGroup as any)[key] = value;
      } else {
        // Update a nested condition
        const conditionIndex = path[path.length - 1];
        current.conditions[conditionIndex] = {
          ...current.conditions[conditionIndex],
          [key]: value
        };
      }
      
      return newGroup;
    });
  };

  // Toggle a group's type between AND/OR
  const toggleGroupType = (path: number[]) => {
    setRootConditionGroup(prevGroup => {
      const newGroup = structuredClone(prevGroup);
      let current: any = newGroup;
      
      // Navigate to the correct nested position
      for (let i = 0; i < path.length; i++) {
        const idx = path[i];
        current = current.conditions[idx];
      }
      
      // Toggle the type
      current.type = current.type === 'AND' ? 'OR' : 'AND';
      
      return newGroup;
    });
  };

  // Add a new condition to a group
  const addCondition = (path: number[]) => {
    setRootConditionGroup(prevGroup => {
      const newGroup = structuredClone(prevGroup);
      let current: any = newGroup;
      
      // Navigate to the correct nested position
      for (let i = 0; i < path.length; i++) {
        const idx = path[i];
        current = current.conditions[idx];
      }
      
      // Add a new empty condition
      current.conditions.push({ field: '', operator: '==', value: '' });
      
      return newGroup;
    });
  };

  // Add a nested group
  const addNestedGroup = (path: number[]) => {
    setRootConditionGroup(prevGroup => {
      const newGroup = structuredClone(prevGroup);
      let current: any = newGroup;
      
      // Navigate to the correct nested position
      for (let i = 0; i < path.length; i++) {
        const idx = path[i];
        current = current.conditions[idx];
      }
      
      // Add a new empty group
      current.conditions.push({
        type: 'AND',
        conditions: [{ field: '', operator: '==', value: '' }]
      });
      
      return newGroup;
    });
  };

  // Remove a condition
  const removeCondition = (path: number[]) => {
    setRootConditionGroup(prevGroup => {
      const newGroup = structuredClone(prevGroup);
      let current: any = newGroup;
      
      // Navigate to the parent of the condition to remove
      for (let i = 0; i < path.length - 1; i++) {
        const idx = path[i];
        parent = current;
        current = current.conditions[idx];
      }
      
      // Remove the condition
      const conditionIndex = path[path.length - 1];
      current.conditions.splice(conditionIndex, 1);
      
      // If the group is now empty, add a default condition
      if (current.conditions.length === 0) {
        current.conditions.push({ field: '', operator: '==', value: '' });
      }
      
      return newGroup;
    });
  };

  // Remove a group
  const removeGroup = (path: number[]) => {
    setRootConditionGroup(prevGroup => {
      const newGroup = structuredClone(prevGroup);
      let current: any = newGroup;
      let parent: any = null;
      
      // Navigate to the parent of the group to remove
      for (let i = 0; i < path.length - 1; i++) {
        const idx = path[i];
        parent = current;
        current = current.conditions[idx];
      }
      
      // Remove the group
      const groupIndex = path[path.length - 1];
      if (parent) {
        parent.conditions.splice(groupIndex, 1);
        
        // If the parent group is now empty, add a default condition
        if (parent.conditions.length === 0) {
          parent.conditions.push({ field: '', operator: '==', value: '' });
        }
      }
      
      return newGroup;
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Voorwaardelijke vereisten configureren</h3>
        <div className="flex gap-2">
          <Button 
            color="primary"
            variant="flat"
            startContent={<TreeStructure size={16} />}
            onClick={() => console.log(JSON.stringify(rootConditionGroup, null, 2))}
          >
            Toon JSON
          </Button>
          <Button 
            color="success"
            startContent={<FloppyDisk size={16} />}
            onClick={handleSave}
          >
            Opslaan
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col gap-2">
        <p className="text-sm">
          Configureer wanneer dit document vereist is gebaseerd op eigenschappen van het {resourceType === 'building' ? 'gebouw' : 'unit'}.
          Gebruik AND/OR groepen om complexe logica te maken.
        </p>
        
        {validationErrors.length > 0 && (
          <div className="bg-[#FFEDED] p-3 rounded-md mb-3">
            <h4 className="text-[#FF3B3B] font-medium mb-1">Los deze problemen op:</h4>
            <ul className="list-disc pl-5 text-sm">
              {validationErrors.map((error, i) => (
                <li key={i} className="text-[#FF3B3B]">{error}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Render the root condition group */}
        {renderConditionGroup(rootConditionGroup)}
      </div>
    </div>
  );
};

export default FileLabelManagementTab;
